import pick from 'lodash/pick';
import { initiatePrivileged, transitionPrivileged, boostAvailability, cancelUpcomingMail, transactionLineItems, freeTransactionCreate, isTransactionNeedingVerificationApi, createQuoteApi, fetchQuoteApi, onCheckUserStripeAccountLinked } from '../../util/api';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import { fetchCurrentUserHasOrdersSuccess, fetchCurrentUser } from '../../ducks/user.duck';
import moment from 'moment';
import { clearCartItems } from '../../ducks/cart.duck';
import { fetchExchangeRates } from '../../ducks/exchangeRates.duck';
import { clearBillingInfoFromSession, clearAbandonedCheckout } from '../../util/sessionStorageManager';
import { sendTransactionNotification } from '../TransactionPage/TransactionPage.duck';
import { DEFAULT_BOOKING_FREE_PROCESS_NAME } from '../../transactions/transaction';
import { makeFreeTransactionData } from '../../util/constants';
import { describeCheckoutError } from './checkoutTelemetry';

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/CheckoutPage/SET_INITIAL_VALUES';

export const INITIATE_ORDER_REQUEST = 'app/CheckoutPage/INITIATE_ORDER_REQUEST';
export const INITIATE_ORDER_SUCCESS = 'app/CheckoutPage/INITIATE_ORDER_SUCCESS';
export const INITIATE_ORDER_ERROR = 'app/CheckoutPage/INITIATE_ORDER_ERROR';

export const CONFIRM_PAYMENT_REQUEST = 'app/CheckoutPage/CONFIRM_PAYMENT_REQUEST';
export const CONFIRM_PAYMENT_SUCCESS = 'app/CheckoutPage/CONFIRM_PAYMENT_SUCCESS';
export const CONFIRM_PAYMENT_ERROR = 'app/CheckoutPage/CONFIRM_PAYMENT_ERROR';

export const SPECULATE_TRANSACTION_REQUEST = 'app/CheckoutPage/SPECULATE_TRANSACTION_REQUEST';
export const SPECULATE_TRANSACTION_SUCCESS = 'app/CheckoutPage/SPECULATE_TRANSACTION_SUCCESS';
export const SPECULATE_TRANSACTION_ERROR = 'app/CheckoutPage/SPECULATE_TRANSACTION_ERROR';
export const SPECULATE_TRANSACTION_CLEAR = 'app/CheckoutPage/SPECULATE_TRANSACTION_CLEAR';

export const STRIPE_CUSTOMER_REQUEST = 'app/CheckoutPage/STRIPE_CUSTOMER_REQUEST';
export const STRIPE_CUSTOMER_SUCCESS = 'app/CheckoutPage/STRIPE_CUSTOMER_SUCCESS';
export const STRIPE_CUSTOMER_ERROR = 'app/CheckoutPage/STRIPE_CUSTOMER_ERROR';

export const INITIATE_INQUIRY_REQUEST = 'app/CheckoutPage/INITIATE_INQUIRY_REQUEST';
export const INITIATE_INQUIRY_SUCCESS = 'app/CheckoutPage/INITIATE_INQUIRY_SUCCESS';
export const INITIATE_INQUIRY_ERROR = 'app/CheckoutPage/INITIATE_INQUIRY_ERROR';

export const FETCH_LINE_ITEMS_REQUEST = 'app/CheckoutPage/FETCH_LINE_ITEMS_REQUEST';
export const FETCH_LINE_ITEMS_SUCCESS = 'app/CheckoutPage/FETCH_LINE_ITEMS_SUCCESS';
export const FETCH_LINE_ITEMS_ERROR = 'app/CheckoutPage/FETCH_LINE_ITEMS_ERROR';

export const CREATE_QUOTE_REQUEST = 'app/CheckoutPage/CREATE_QUOTE_REQUEST';
export const CREATE_QUOTE_SUCCESS = 'app/CheckoutPage/CREATE_QUOTE_SUCCESS';
export const CREATE_QUOTE_ERROR = 'app/CheckoutPage/CREATE_QUOTE_ERROR';

export const FETCH_QUOTE_REQUEST = 'app/CheckoutPage/FETCH_QUOTE_REQUEST';
export const FETCH_QUOTE_SUCCESS = 'app/CheckoutPage/FETCH_QUOTE_SUCCESS';
export const FETCH_QUOTE_ERROR = 'app/CheckoutPage/FETCH_QUOTE_ERROR';

// ================ Reducer ================ //

const initialState = {
  listing: null,
  withinNinetyDays: null,
  orderData: null,
  speculateTransactionInProgress: false,
  speculateTransactionError: null,
  speculatedTransaction: null,
  transaction: null,
  initiateOrderError: null,
  confirmPaymentError: null,
  stripeCustomerFetched: false,
  initiateInquiryInProgress: false,
  initiateInquiryError: null,
  lineItems: null,
  fetchLineItemsInProgress: false,
  fetchLineItemsError: null,
  returnCareInstructions: [],
  quoteId: null,
  createQuoteInProgress: false,
  createQuoteError: null,
  quoteData: null,
  fetchQuoteInProgress: false,
  fetchQuoteError: null,
};

export default function checkoutPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case SPECULATE_TRANSACTION_REQUEST:
      return {
        ...state,
        speculateTransactionInProgress: true,
        speculateTransactionError: null,
        speculatedTransaction: null,
      };
    case SPECULATE_TRANSACTION_SUCCESS:
      return {
        ...state,
        speculateTransactionInProgress: false,
        speculatedTransaction: payload.transaction,
      };
    case SPECULATE_TRANSACTION_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return {
        ...state,
        speculateTransactionInProgress: false,
        speculateTransactionError: payload,
      };
    case SPECULATE_TRANSACTION_CLEAR:
      return {
        ...state,
        speculateTransactionInProgress: false,
        speculateTransactionError: null,
        speculatedTransaction: null,
      };

    case INITIATE_ORDER_REQUEST:
      return { ...state, initiateOrderError: null };
    case INITIATE_ORDER_SUCCESS:
      return { ...state, transaction: payload.order };
    case INITIATE_ORDER_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, initiateOrderError: payload };

    case CONFIRM_PAYMENT_REQUEST:
      return { ...state, confirmPaymentError: null };
    case CONFIRM_PAYMENT_SUCCESS:
      return state;
    case CONFIRM_PAYMENT_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, confirmPaymentError: payload };

    case STRIPE_CUSTOMER_REQUEST:
      return { ...state, stripeCustomerFetched: false };
    case STRIPE_CUSTOMER_SUCCESS:
      return { ...state, stripeCustomerFetched: true };
    case STRIPE_CUSTOMER_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, stripeCustomerFetchError: payload };

    case INITIATE_INQUIRY_REQUEST:
      return { ...state, initiateInquiryInProgress: true, initiateInquiryError: null };
    case INITIATE_INQUIRY_SUCCESS:
      return { ...state, initiateInquiryInProgress: false };
    case INITIATE_INQUIRY_ERROR:
      return { ...state, initiateInquiryInProgress: false, initiateInquiryError: payload };

    case FETCH_LINE_ITEMS_REQUEST:
      return {
        ...state,
        fetchLineItemsInProgress: true,
        fetchLineItemsError: null,
      };
    case FETCH_LINE_ITEMS_SUCCESS:
      return {
        ...state,
        fetchLineItemsInProgress: false,
        lineItems: payload,
      };
    case FETCH_LINE_ITEMS_ERROR:
      return {
        ...state,
        fetchLineItemsInProgress: false,
        fetchLineItemsError: payload,
      };

    case CREATE_QUOTE_REQUEST:
      return { ...state, createQuoteInProgress: true, createQuoteError: null };
    case CREATE_QUOTE_SUCCESS:
      return { ...state, createQuoteInProgress: false, quoteId: action.payload };
    case CREATE_QUOTE_ERROR:
      return { ...state, createQuoteInProgress: false, createQuoteError: action.payload };

    case FETCH_QUOTE_REQUEST:
      return { ...state, fetchQuoteInProgress: true, fetchQuoteError: null };
    case FETCH_QUOTE_SUCCESS:
      return { ...state, fetchQuoteInProgress: false, quoteData: action.payload };
    case FETCH_QUOTE_ERROR:
      return { ...state, fetchQuoteInProgress: false, fetchQuoteError: action.payload };

    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

const initiateOrderRequest = () => ({ type: INITIATE_ORDER_REQUEST });

const initiateOrderSuccess = data => ({
  type: INITIATE_ORDER_SUCCESS,
  payload: data,
});

export const initiateOrderError = e => ({
  type: INITIATE_ORDER_ERROR,
  error: true,
  payload: e,
});

const confirmPaymentRequest = () => ({ type: CONFIRM_PAYMENT_REQUEST });

const confirmPaymentSuccess = orderId => ({
  type: CONFIRM_PAYMENT_SUCCESS,
  payload: orderId,
});

const confirmPaymentError = e => ({
  type: CONFIRM_PAYMENT_ERROR,
  error: true,
  payload: e,
});

export const speculateTransactionRequest = () => ({ type: SPECULATE_TRANSACTION_REQUEST });

export const speculateTransactionSuccess = transaction => ({
  type: SPECULATE_TRANSACTION_SUCCESS,
  payload: { transaction },
});

export const speculateTransactionError = e => ({
  type: SPECULATE_TRANSACTION_ERROR,
  error: true,
  payload: e,
});

export const stripeCustomerRequest = () => ({ type: STRIPE_CUSTOMER_REQUEST });
export const stripeCustomerSuccess = () => ({ type: STRIPE_CUSTOMER_SUCCESS });
export const stripeCustomerError = e => ({
  type: STRIPE_CUSTOMER_ERROR,
  error: true,
  payload: e,
});

export const initiateInquiryRequest = () => ({ type: INITIATE_INQUIRY_REQUEST });
export const initiateInquirySuccess = () => ({ type: INITIATE_INQUIRY_SUCCESS });
export const initiateInquiryError = e => ({
  type: INITIATE_INQUIRY_ERROR,
  error: true,
  payload: e,
});

export const fetchLineItemsRequest = () => ({ type: FETCH_LINE_ITEMS_REQUEST });
export const fetchLineItemsSuccess = lineItems => ({
  type: FETCH_LINE_ITEMS_SUCCESS,
  payload: lineItems,
});
export const fetchLineItemsError = e => ({
  type: FETCH_LINE_ITEMS_ERROR,
  error: true,
  payload: e,
});

/* ================ Thunks ================ */

export const initiateOrder = (
  orderParams,
  processAlias,
  transactionId,
  transitionName,
  isPrivilegedTransition
) => (dispatch, getState, sdk) => {
  dispatch(initiateOrderRequest());

  // If we already have a transaction ID, we should transition, not
  // initiate.
  const isTransition = !!transactionId;

  const { deliveryMethod, paymentIntentData, itemsParams, ...otherOrderParams } = orderParams;

  // Parameters only for client app's server
  const orderData = deliveryMethod ? { deliveryMethod, paymentIntentData } : { paymentIntentData };

  // Parameters for Marketplace API
  const transitionParams = {
    itemsParams,
    ...otherOrderParams,
  };

  const bodyParams = isTransition
    ? {
      id: transactionId,
      transition: transitionName,
      params: transitionParams,
      processAlias,
    }
    : {
      processAlias,
      transition: transitionName,
      params: transitionParams,
    };
  const queryParams = {
    include: ['booking', 'provider', 'customer', 'listing'],
    expand: true,
  };


  const handleSucces = async response => {
    const entities = denormalisedResponseEntities(response);
    const order = entities[0];

    // Count the number of times current user made transaction 
    // in a year, for the google analytics.
    let count = 1;
    try {
      const transactionsRes = await sdk.transactions.query({
        only: 'order',
        createdAtStart: moment().startOf('year').toISOString(),
      });
      count = transactionsRes?.data?.data?.length;

      // Restore seats
      const listing = order?.listing;
      if (listing?.id) {
        boostAvailability({
          listingId: listing.id.uuid,
        })
          .catch(err => console.log('Failed to restore listing availability', err))
      }

      // send onesignal notification
      sendTransactionNotification({
        getState,
        txId: order?.id?.uuid,
        transitionName,
        transaction: order,
      })

    } catch (err) {
      // Same rule as below: these are side effects, not the payment. Report them, but
      // never let them reject - a throw here would abort checkout before the charge.
      log.error(err, 'checkout-post-success-side-effect-failed', {
        step: 'transactionCount/boostAvailability/pushNotification',
      });
    };
    // Post-success housekeeping. By this point the booking and its PaymentIntent
    // already exist on the server, so NOTHING below may reject: throwing here rejects
    // the whole checkout sequence before the card is ever charged, which leaves an
    // unpaid transaction to expire silently 15 minutes later and makes the next submit
    // start a brand-new one. (2026-09-03: a null currentUser threw here and produced
    // six orphaned bookings for a single customer before one attempt got through.)
    try {
      const { currentUser } = getState().user;
      const userCreatedAt = currentUser?.attributes?.createdAt;
      const currentUserId = currentUser?.id?.uuid;
      // NB: moment().diff(undefined) resolves undefined to "now" and returns 0, so
      // without the explicit userCreatedAt guard this branch is entered precisely when
      // currentUser is missing - the one case where the call below cannot work.
      const isWithin1Hour = !!userCreatedAt && moment().diff(userCreatedAt, 'minutes') < 60;
      if (isWithin1Hour && currentUserId) {
        cancelUpcomingMail({ id: currentUserId }).catch(err =>
          log.error(err, 'checkout-post-success-side-effect-failed', { step: 'cancelUpcomingMail' })
        );
      }
    } catch (err) {
      // Never fail a paid-for booking because housekeeping broke.
      log.error(err, 'checkout-post-success-side-effect-failed', { step: 'cancelUpcomingMail' });
    }

    dispatch(initiateOrderSuccess({ order, count }));
    dispatch(fetchCurrentUserHasOrdersSuccess(true));
    return order;
  };

  const handleError = e => {
    dispatch(initiateOrderError(storableError(e)));
    const transactionIdMaybe = transactionId ? { transactionId: transactionId.uuid } : {};
    // Distinct code per step: a failure of the payment request (which creates the
    // PaymentIntent and starts the 15-minute clock) is a different problem from a
    // plain booking-request failure, and must be filterable as such.
    const isPaymentRequest =
      typeof transitionName === 'string' && transitionName.includes('request-payment');
    const code = isPaymentRequest ? 'checkout-request-payment-failed' : 'initiate-order-failed';
    log.error(e, code, {
      ...transactionIdMaybe,
      listingId: orderParams?.listingId?.uuid,
      transition: transitionName,
      isTransition,
      ...describeCheckoutError(e),
      ...orderData,
    });
    throw e;
  };

  if (isTransition && isPrivilegedTransition) {
    // transition privileged
    return transitionPrivileged({ isSpeculative: false, orderData, bodyParams, queryParams })
      .then(handleSucces)
      .then(order => {
        // Clear the billing info from session
        // Clear abandoned checkout data
        clearAbandonedCheckout();
        // Clear the cart items related to the author if exists
        const itemsIds = order?.attributes?.protectedData?.itemsIds;
        if (itemsIds?.length) {
          dispatch(clearCartItems(itemsIds));
        }
        return order;
      })
      .catch(handleError);
  } else if (isTransition) {
    // transition non-privileged
    return sdk.transactions
      .transition(bodyParams, queryParams)
      .then(handleSucces)
      .catch(handleError);
  } else if (isPrivilegedTransition) {
    // initiate privileged
    return initiatePrivileged({ isSpeculative: false, orderData, bodyParams, queryParams })
      .then(handleSucces)
      .then(order => {
        // Clear the cart items related to the author if exists
        clearBillingInfoFromSession();
        // Clear abandoned checkout data
        clearAbandonedCheckout();
        const itemsIds = order?.attributes?.protectedData?.itemsIds;
        if (itemsIds?.length) {
          dispatch(clearCartItems(itemsIds));
        }
        return order;
      })
      .catch(handleError);
  } else {
    // initiate non-privileged
    return sdk.transactions
      .initiate(bodyParams, queryParams)
      .then(handleSucces)
      .catch(handleError);
  }
};

export const confirmPayment = (transactionId, transitionName, transitionParams = {}) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(confirmPaymentRequest());

  const bodyParams = {
    id: transactionId,
    transition: transitionName,
    params: transitionParams,
  };
  const queryParams = {
    include: ['booking', 'provider','listing'],
    expand: true,
  };

  return sdk.transactions
    .transition(bodyParams, queryParams)
    .then(response => {
      const order = response.data.data;
      // Creating the free transaction if process name is free
      try {
        const currentUser = getState().user.currentUser;
        isTransactionNeedingVerificationApi({ transactionId: order.id.uuid })
        if (order.attributes.processName == DEFAULT_BOOKING_FREE_PROCESS_NAME) {
          // The record is still sent so the server can store it as before. The
          // transaction ID lets the server build and check its own version.
          const mongoData = makeFreeTransactionData(response, currentUser);
          freeTransactionCreate({ transactionId: order.id.uuid, payload: mongoData })
            .then(() => {
              console.log('Wallet booking recorded');
            })
            .catch(e => {
              console.log(e, " Error");
            })
        }
        
      } catch (e) {
        console.log(e, "e");
      } finally {
        // Clear cart items after successful payment confirmation
        const itemsIds = order?.attributes?.protectedData?.itemsIds;
        if (itemsIds?.length) {
          clearBillingInfoFromSession();
          clearAbandonedCheckout();
          dispatch(clearCartItems(itemsIds));
        }
        
        dispatch(confirmPaymentSuccess(order.id));
        return order;
      }
    })
    .catch(e => {
      dispatch(confirmPaymentError(storableError(e)));
      const transactionIdMaybe = transactionId ? { transactionId: transactionId.uuid } : {};
      // Own code for this step: the card has already been confirmed with Stripe
      // by the time this runs, so a failure here strands a paid-but-unrecorded
      // transaction — not the same thing as an initiate failure.
      log.error(e, 'checkout-confirm-payment-failed', {
        ...transactionIdMaybe,
        transition: transitionName,
        ...describeCheckoutError(e),
      });
      throw e;
    });
};

export const sendMessage = params => (dispatch, getState, sdk) => {
  const message = params.message;
  const orderId = params.id;

  if (message) {
    return sdk.messages
      .send({ transactionId: orderId, content: message })
      .then(() => {
        return { orderId, messageSuccess: true };
      })
      .catch(e => {
        log.error(e, 'initial-message-send-failed', { txId: orderId });
        return { orderId, messageSuccess: false };
      });
  } else {
    return Promise.resolve({ orderId, messageSuccess: true });
  }
};

/**
 * Initiate transaction against default-inquiry process
 * Note: At this point inquiry transition is made directly against Marketplace API.
 *       So, client app's server is not involved here unlike with transitions including payments.
 *
 * @param {*} inquiryParams contains listingId and protectedData
 * @param {*} processAlias 'default-inquiry/release-1'
 * @param {*} transitionName 'transition/inquire-without-payment'
 * @returns
 */
export const initiateInquiryWithoutPayment = (inquiryParams, processAlias, transitionName) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(initiateInquiryRequest());

  if (!processAlias) {
    const error = new Error('No transaction process attached to listing');
    log.error(error, 'listing-process-missing', {
      listingId: listing?.id?.uuid,
    });
    dispatch(initiateInquiryError(storableError(error)));
    return Promise.reject(error);
  }

  const bodyParams = {
    transition: transitionName,
    processAlias,
    params: inquiryParams,
  };
  const queryParams = {
    include: ['provider'],
    expand: true,
  };

  return sdk.transactions
    .initiate(bodyParams, queryParams)
    .then(response => {
      const transactionId = response.data.data.id;
      dispatch(initiateInquirySuccess());
      return transactionId;
    })
    .catch(e => {
      dispatch(initiateInquiryError(storableError(e)));
      throw e;
    });
};

/**
 * Initiate or transition the speculative transaction with the given
 * booking details
 *
 * The API allows us to do speculative transaction initiation and
 * transitions. This way we can create a test transaction and get the
 * actual pricing information as if the transaction had been started,
 * without affecting the actual data.
 *
 * We store this speculative transaction in the page store and use the
 * pricing info for the booking breakdown to get a proper estimate for
 * the price with the chosen information.
 */
export const speculateTransaction = (
  orderParams,
  processAlias,
  transactionId,
  transitionName,
  isPrivilegedTransition
) => (dispatch, getState, sdk) => {
  dispatch(speculateTransactionRequest());

  // If we already have a transaction ID, we should transition, not
  // initiate.
  const isTransition = !!transactionId;

  const { deliveryMethod, itemsParams, ...otherOrderParams } = orderParams;

  // Parameters only for client app's server
  const orderData = deliveryMethod ? { deliveryMethod } : {};

  // Parameters for Marketplace API
  const transitionParams = {
    itemsParams,
    ...otherOrderParams,
    cardToken: 'CheckoutPage_speculative_card_token',
  };

  const bodyParams = isTransition
    ? {
      id: transactionId,
      transition: transitionName,
      params: transitionParams,
    }
    : {
      processAlias,
      transition: transitionName,
      params: transitionParams,
    };

  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };

  const handleSuccess = response => {
    const entities = denormalisedResponseEntities(response);
    if (entities.length !== 1) {
      throw new Error('Expected a resource in the speculate response');
    }
    const tx = entities[0];
    dispatch(speculateTransactionSuccess(tx));
  };

  const handleError = e => {
    if (e.status === 409) {
      // Check whether Sharetribe returned a specific error code in the response body.
      // Errors like transaction-booking-time-not-available carry a code; the same-owner
      // conflict (when the listing author accidentally triggers speculation) does not.
      const apiErrors = e?.data?.errors || [];
      const hasSpecificCode = apiErrors.some(err => err.code);

      if (!hasSpecificCode) {
        // Same-owner 409 — CheckoutPage.js guard normally prevents this but a race condition
        // can slip it through. Clear the in-progress flag so the page renders using the
        // lineItems fallback for the price breakdown; don't show an error to the user.
        dispatch({ type: SPECULATE_TRANSACTION_CLEAR });
        return;
      }
    }

    log.error(e, 'speculate-transaction-failed', {});
    return dispatch(speculateTransactionError(storableError(e)));
  };

  if (isTransition && isPrivilegedTransition) {
    // transition privileged
    return transitionPrivileged({ isSpeculative: true, orderData, bodyParams, queryParams })
      .then(handleSuccess)
      .catch(handleError);
  } else if (isTransition) {
    // transition non-privileged
    return sdk.transactions
      .transitionSpeculative(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  } else if (isPrivilegedTransition) {
    // initiate privileged
    return initiatePrivileged({ isSpeculative: true, orderData, bodyParams, queryParams })
      .then(handleSuccess)
      .catch(handleError);
  } else {
    // initiate non-privileged
    return sdk.transactions
      .initiateSpeculative(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  }
};

// StripeCustomer is a relantionship to currentUser
// We need to fetch currentUser with correct params to include relationship
export const stripeCustomer = () => (dispatch, getState, sdk) => {
  dispatch(stripeCustomerRequest());

  return dispatch(fetchCurrentUser({ include: ['stripeCustomer.defaultPaymentMethod'] }))
    .then(response => {
      dispatch(stripeCustomerSuccess());
    })
    .catch(e => {
      dispatch(stripeCustomerError(storableError(e)));
    });
};

export const fetchTransactionLineItems = (fetchLineItemsData) => dispatch => {
  dispatch(fetchLineItemsRequest());
  return transactionLineItems(fetchLineItemsData)
    .then(response => {
      const lineItems = response.data;
      dispatch(fetchLineItemsSuccess(lineItems));
      return lineItems;
    })
    .catch(e => {
      dispatch(fetchLineItemsError(storableError(e)));
      log.error(e, 'fetching-line-items-failed', {});
      throw e;
    });
};


// ================ Quote thunks ================ //

export const createQuoteThunk = ({ orderData, listing, storageKey }) => async (dispatch, getState) => {
  dispatch({ type: CREATE_QUOTE_REQUEST });
  try {
    const items = orderData
      .filter(item => !!item.listing?.id?.uuid)
      .map(item => ({
        listingId: item.listing.id.uuid,
        bookingStart: item.bookingDates?.bookingStart,
        bookingEnd: item.bookingDates?.bookingEnd,
        package: item.package,
        selectedFeeTypes: item.selectedFeeTypes || [],
        quantity: item.seats || item.quantity || 1,
        pickupAtOwnerLocation: item.pickupAtOwnerLocation || false,
        returnAtOwnerLocation: item.returnAtOwnerLocation || false,
      }));

    if (!items.length) {
      dispatch({ type: CREATE_QUOTE_ERROR, payload: new Error('No valid items for quote') });
      return;
    }
    const authorProfile = listing?.author?.attributes?.profile;
    const storeName =
      authorProfile?.publicData?.storeName ||
      authorProfile?.displayName ||
      (authorProfile?.firstName && authorProfile?.lastName
        ? `${authorProfile.firstName} ${authorProfile.lastName}`
        : authorProfile?.firstName || authorProfile?.lastName || null);
    const { quoteId } = await createQuoteApi({ items, storeName });
    // Update browser URL bar silently — window.history avoids React Router re-render
    const currentUrl = window.location.href;
    const separator = currentUrl.includes('?') ? '&' : '?';
    window.history.replaceState({}, '', `${currentUrl}${separator}quoteId=${quoteId}`);
    // Mark this browser session as the sender so refresh is handled correctly
    if (storageKey) {
      sessionStorage.setItem(`${storageKey}_quoteSender`, quoteId);
    }

    // Prevent FloatingCheckoutWidget from treating this quote session as an abandoned checkout
    clearAbandonedCheckout();

    dispatch({ type: CREATE_QUOTE_SUCCESS, payload: quoteId });
  } catch (err) {
    dispatch({ type: CREATE_QUOTE_ERROR, payload: err });
  }
};

// Full fetch: used by recipient on first load
// Fetches quote metadata + live listings + line items, then populates checkout state
export const fetchQuoteThunk = (quoteId, config) => async (dispatch, getState, sdk) => {
  dispatch({ type: FETCH_QUOTE_REQUEST });
  try {
    const quote = await fetchQuoteApi(quoteId);

    // Fetch all listings live from Sharetribe — use allSettled so one unavailable listing
    // (deleted, closed) doesn't collapse the entire quote for the recipient.
    const listingResults = await Promise.allSettled(
      quote.items.map(item =>
        sdk.listings.show({ id: item.listingId, include: ['author', 'images', 'currentStock'] })
      )
    );

    // Pair each settled result with its quote item; discard any that failed.
    const validPairs = listingResults
      .map((result, i) => ({ result, item: quote.items[i] }))
      .filter(({ result }) => result.status === 'fulfilled');

    if (!validPairs.length) {
      // Every listing is unavailable — the quote cannot be fulfilled.
      dispatch({ type: FETCH_QUOTE_ERROR, payload: 'NOT_FOUND' });
      return;
    }

    // sdk.listings.show puts related entities in r.data.included (not on r.data.data directly).
    // Attach the full author and images onto each listing so downstream components don't crash.
    const listings = validPairs.map(({ result }) => {
      const listing = result.value.data.data;
      const included = result.value.data.included || [];

      const authorRef = listing.relationships?.author?.data;
      if (authorRef) {
        const author = included.find(
          e => e != null && e.type === 'user' && e.id.uuid === authorRef.id.uuid
        );
        if (author) listing.author = author;
      }

      const imageRefs = listing.relationships?.images?.data || [];
      listing.images = imageRefs
        .map(ref => included.find(e => e != null && e.type === 'image' && e.id.uuid === ref.id.uuid))
        .filter(Boolean);

      return listing;
    });

    // Build orderData in the same shape startCheckoutFromItems produces
    const orderData = validPairs.map(({ item }, i) => ({
      listing: listings[i],
      bookingDates: {
        bookingStart: new Date(item.bookingStart),
        bookingEnd: new Date(item.bookingEnd),
      },
      packageName: item.package,
      selectedFeeTypes: item.selectedFeeTypes || [],
      seats: item.quantity || 1,
      pickupAtOwnerLocation: item.pickupAtOwnerLocation,
      returnAtOwnerLocation: item.returnAtOwnerLocation,
    }));

    const baseListing = listings[0];

    // Mirror the Stripe-account check done in checkoutFromCart.js and ListingPage.shared.js.
    // If the provider hasn't connected Stripe, route to the free process so checkout works.
    // Isolated in its own try/catch — a Stripe API blip must not surface as "Quote not found".
    let isAuthorStripeAccountLinked = true;
    try {
      ({ isAuthorStripeAccountLinked } = await onCheckUserStripeAccountLinked({
        userId: baseListing.author.id.uuid,
      }));
    } catch {
      // Non-fatal — default to paid process; worst case recipient sees the paid checkout flow
    }
    if (!isAuthorStripeAccountLinked) {
      Object.assign(baseListing.attributes.publicData, {
        transactionProcessAlias: DEFAULT_BOOKING_FREE_PROCESS_NAME + '/release-1',
      });
    }

    // Fetch line items — required for unauthenticated recipients since speculateTransaction
    // won't run without auth; without this the price breakdown would be blank
    let lineItems = null;
    try {
      // Server expects { fetchLineItemsData: [{ orderData: {...}, listingId, isOwnListing }] }
      const fetchLineItemsData = orderData.map(item => ({
        orderData: {
          bookingStart: item.bookingDates?.bookingStart,
          bookingEnd: item.bookingDates?.bookingEnd,
          selectedFeeTypes: item.selectedFeeTypes || [],
          package: item.packageName,
          seats: item.seats,
          pickupAtOwnerLocation: item.pickupAtOwnerLocation,
          returnAtOwnerLocation: item.returnAtOwnerLocation,
        },
        listingId: item.listing?.id?.uuid,
        isOwnListing: false,
      }));
      const lineItemsResponse = await transactionLineItems({ fetchLineItemsData });
      lineItems = lineItemsResponse.data;
    } catch (lineItemErr) {
      // Non-fatal — breakdown will be blank but booking still works
      console.warn('fetchQuoteThunk: lineItems fetch failed', lineItemErr);
    }

    // Compute withinNinetyDays from the latest booking end date across all items
    const latestEnd = orderData.reduce((latest, item) => {
      const end = item.bookingDates?.bookingEnd;
      return end && end > latest ? end : latest;
    }, new Date(0));
    const withinNinetyDays = (latestEnd - Date.now()) / (1000 * 60 * 60 * 24) <= 90;

    // Populate checkout Redux state — same mechanism as navigating from cart
    dispatch(setInitialValues({ listing: baseListing, orderData, lineItems, withinNinetyDays }));
    dispatch({ type: FETCH_QUOTE_SUCCESS, payload: quote });
  } catch (err) {
    // The client API helper sets httpStatus (not status) on thrown errors.
    const httpStatus = err?.httpStatus ?? err?.status;
    const error = httpStatus === 404 ? 'NOT_FOUND' : 'ERROR';
    dispatch({ type: FETCH_QUOTE_ERROR, payload: error });
  }
};

// Banner-only fetch: used by recipient on refresh
// pageData is already in sessionStorage — only reloads quote metadata for the banner
export const fetchQuoteBannerOnly = quoteId => async dispatch => {
  dispatch({ type: FETCH_QUOTE_REQUEST });
  try {
    const quote = await fetchQuoteApi(quoteId);
    dispatch({ type: FETCH_QUOTE_SUCCESS, payload: quote });
  } catch (err) {
    const httpStatus = err?.httpStatus ?? err?.status;
    const error = httpStatus === 404 ? 'NOT_FOUND' : 'ERROR';
    dispatch({ type: FETCH_QUOTE_ERROR, payload: error });
  }
};

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  // Fetch exchange rates, do not fetch if already exists.
  const { exchangeRates } = getState().exchangeRate;

  if (typeof window != 'undefined' && exchangeRates && exchangeRates.length == 0) {
    return dispatch(fetchExchangeRates());
  };

  return Promise.resolve();
};